import {Component, computed, DestroyRef, inject, Injector, input, output, signal, AfterViewInit, model} from '@angular/core';
import {toObservable, takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {debounceTime, distinctUntilChanged, filter} from 'rxjs';
import {IItem} from '../../../../types/IItem';
import {Modal} from '../../../../shared/components/modal/modal';
import {ResultsStore} from '../../../../shared/store/results.store';
import {AddonRequest} from '../../../../shared/services/items.service';
import {IAvailability} from '../../../../types/IAvailability';
import {IAvailabilitySource} from '../../../../types/IAvailabilitySource';
import {getAvailable, getFirstMap} from '../../../../shared/utils/availability.utils';
import {CheckboxComponent} from '../../../../shared/components/checkbox/checkbox.component';
import {CurrencyPipe} from '@angular/common';
import {ButtonComponent} from '../../../../shared/components/button/button.component';
import {IconsComponent} from '../../../../shared/components/icons/icons.component';
import {Number} from '../../../../shared/components/number/number';
import {FormsModule} from '@angular/forms';
import {Card} from '../../../../shared/components/card/card';
import {DialogService} from '../../../../shared/services/dialog.service';
import {LinkComponent} from '../../../../shared/components/link/link.component';
import {ResultTableService} from '../../services/result-table.service';
import {EventService} from '../../../../shared/services/event.service';
import {NavigationService} from '../../../../shared/services/navigation.service';
import {AccessService} from '../../../../shared/services/access.service';

interface SupplierRow extends IAvailability {
  checked: boolean;
  selected_quantity: number;
}

interface VendorGroup {
  vendorName: string;
  label: string;
  sources: SupplierRow[];
  checked: boolean;
  selected_quantity: number;
  total_available: number;
  cost: number;
}

@Component({
  selector: 'app-results-item-supplier-selection-modal',
  imports: [Modal, CheckboxComponent, CurrencyPipe, ButtonComponent, IconsComponent, Number, FormsModule, Card, LinkComponent],
  templateUrl: './results-item-supplier-selection-modal.html',
  styleUrl: './results-item-supplier-selection-modal.css',
})
export class ResultsItemSupplierSelectionModal implements AfterViewInit {
  item = model.required<IItem>();
  isOpen = input.required<boolean>();
  isOpenChange = output<void>();
  itemSelected = output<IItem>();

  resultsStore = inject(ResultsStore);
  resultTableService = inject(ResultTableService);
  access = inject(AccessService);

  private injector = inject(Injector);
  private destroyRef = inject(DestroyRef);
  private dialogService = inject(DialogService);
  private eventService = inject(EventService);
  private navigationService = inject(NavigationService);

  private expandedGroups = signal<Set<string>>(new Set());
  private animatingGroups = signal<Set<string>>(new Set());

  groups = signal<VendorGroup[]>([]);

  ngAfterViewInit(): void {
    toObservable(this.isOpen, {injector: this.injector}).pipe(
      filter(open => open),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => this.groups.set(this.initializeGroups()));

    // Refetch whenever the weighted price or quantity settles, including when pricing for a newly
    // entered cost arrives after the selection changed.
    toObservable(this.addonRequest, {injector: this.injector}).pipe(
      debounceTime(500),
      filter((request): request is AddonRequest => request !== null),
      distinctUntilChanged((a, b) => this.addonKey(a) === this.addonKey(b)),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(request => this.resultsStore.fetchAddons([request]));
  }

  priceFor(cost: number): number {
    return this.resultsStore.getPricing()(this.item().id, cost)?.price ?? 0;
  }

  isGroupExpanded(vendorName: string): boolean {
    return this.expandedGroups().has(vendorName);
  }

  isGroupExpandedOrAnimating(vendorName: string): boolean {
    return this.expandedGroups().has(vendorName) || this.animatingGroups().has(vendorName);
  }

  isSourceHidden(group: VendorGroup, index: number): boolean {
    return group.sources.length >= 4 && index >= 2 && !this.isGroupExpanded(group.vendorName);
  }

  toggleGroup(vendorName: string): void {
    const collapsing = this.expandedGroups().has(vendorName);
    this.expandedGroups.update(set => {
      const next = new Set(set);
      collapsing ? next.delete(vendorName) : next.add(vendorName);
      return next;
    });
    if (collapsing) {
      this.animatingGroups.update(set => new Set(set).add(vendorName));
      setTimeout(() => {
        this.animatingGroups.update(set => {
          const next = new Set(set);
          next.delete(vendorName);
          return next;
        });
      }, 500);
    }
  }

  private selectedGroups = computed(() => this.groups().filter(g => g.checked && g.selected_quantity > 0));

  private selectedQuantity = computed(() => this.selectedGroups().reduce((sum, g) => sum + g.selected_quantity, 0));

  private weightedPrice = computed(() => {
    const totalQty = this.selectedQuantity();
    if (totalQty === 0) return null;
    return +(this.selectedGroups().reduce((sum, g) => sum + g.selected_quantity * this.priceFor(g.cost), 0) / totalQty).toFixed(2);
  });

  private addonRequest = computed((): AddonRequest | null => {
    const price = this.weightedPrice();
    const priceLevel = this.resultsStore.currentPriceLevel();
    if (!this.isOpen() || price === null || !priceLevel) return null;

    const item = this.item();
    return {
      item_id: item.id,
      brand: item.brand,
      model: item.model,
      item_number: item.item_number,
      price,
      quantity: this.selectedQuantity(),
      price_level_id: String(priceLevel.id),
    };
  });

  otdForItem = computed(() => {
    const price = this.weightedPrice();
    if (price === null) return null;
    const addons = this.resultsStore.getAddonsForItem()(this.item().id, price, this.selectedQuantity());
    return Object.values(addons?.out_the_door ?? {});
  });

  onCheckboxChange(group: VendorGroup, checked: boolean): void {
    this.groups.update(groups => groups.map(g => g.vendorName !== group.vendorName ? g : {
      ...g,
      checked,
      selected_quantity: checked ? g.selected_quantity : 0,
    }));
  }

  onQuantityChange(group: VendorGroup, quantity: string | number): void {
    this.groups.update(groups => groups.map(g => g.vendorName !== group.vendorName ? g : {
      ...g,
      selected_quantity: Math.min(Math.max(0, +quantity), g.total_available),
      checked: Math.min(Math.max(0, +quantity), g.total_available) > 0,
    }));
  }

  onCostChange(group: VendorGroup, cost: string | number): void {
    this.groups.update(groups => groups.map(g => g.vendorName !== group.vendorName ? g : {
      ...g,
      cost: +cost,
      sources: g.sources.map(s => s.type === 'external' ? {...s, cost: +cost} : s),
    }));
    this.fetchPricing([+cost]);
  }

  protected readonly getAvailable = getAvailable;

  private addonKey(request: AddonRequest): string {
    return `${request.item_id}_${request.price_level_id}_${request.price}_${request.quantity}`;
  }

  private initializeRows(): SupplierRow[] {
    const item = this.item();
    const totalNeeded = this.resultsStore.quantity();
    const sourcedMap = new Map(item.sourcing.map(src => [`${src.type}-${src.name}`, src]));

    let remainingToFill = totalNeeded;
    sourcedMap.forEach(src => {
      remainingToFill -= src.selected_quantity;
    });
    remainingToFill = Math.max(remainingToFill, 0);

    return item.availability.filter(source => getAvailable(source)).map(source => {
      const key = `${source.type}-${source.name}`;
      const sourced = sourcedMap.get(key);

      if (sourced) {
        return {
          ...source,
          checked: true,
          selected_quantity: sourced.selected_quantity,
          cost: sourced.cost,
        };
      }

      const qtyToFill = Math.min(remainingToFill, getAvailable(source));
      remainingToFill -= qtyToFill;
      return {
        ...source,
        checked: qtyToFill > 0,
        selected_quantity: qtyToFill,
      };
    });
  }

  private initializeGroups(): VendorGroup[] {
    const groupMap = new Map<string, VendorGroup>();

    for (const row of this.initializeRows()) {
      const isDistributor = row.type === 'distributor';
      const key = isDistributor ? (row.vendor?.name ?? row.name) : `${row.type}-${row.name}`;
      const label = isDistributor ? (row.vendor?.name ?? row.name) : (row.type === 'transfer' ? row.name : this.getDisplayType(row.type));

      if (!groupMap.has(key)) {
        groupMap.set(key, {
          vendorName: key,
          label,
          sources: [],
          checked: false,
          selected_quantity: 0,
          total_available: 0,
          cost: row.cost,
        });
      }

      const group = groupMap.get(key)!;
      group.sources.push(row);
      group.total_available += getAvailable(row);
      if (row.checked) {
        group.checked = true;
        group.selected_quantity += row.selected_quantity;
      }
    }

    return Array.from(groupMap.values());
  }

  private getDisplayType(type: IAvailability['type']): string {
    switch (type) {
      case 'local':
        return 'In Stock';
      case 'transfer':
        return 'Transfer';
      case 'distributor':
        return 'Supplier';
      case 'external':
        return 'External';
    }
  }

  private fetchPricing(costs: number[]): void {
    this.resultsStore.fetchPricing({
      price_level_id: this.resultsStore.currentPriceLevel()?.id,
      customer_id: undefined,
      repair_order_id: undefined,
      items: [{
        item_id: this.item().id,
        brand: this.item().brand,
        model: this.item().model,
        item_number: this.item().item_number,
        costs,
        map: getFirstMap([...this.item().sourcing ?? [], ...this.item().availability]),
      }],
    });
  }

  onSelect(): void {
    const sourcing = this.getSourcing();

    if (sourcing) {
      this.resultsStore.setQuantity(sourcing.reduce((sum, s) => sum + s.selected_quantity, 0));
      this.fetchPricing([...new Set(this.item().sourcing.map(({cost}) => cost)).add(this.item().cost)]);
      this.itemSelected.emit({...this.item(), sourcing});
      this.isOpenChange.emit();
    }
  }

  onQuickAdd() {
    const sourcing = this.getSourcing();
    if (sourcing) {
      this.eventService.emit('add-item', {item: {...this.item(), sourcing}, addon: null, mode: 'quick'});
      this.isOpenChange.emit();
    }
  }

  onAddToQuote() {
    const sourcing = this.getSourcing();

    if (sourcing) {
      this.resultsStore.clearCompare();
      this.resultsStore.addCompareItem({...this.item(), sourcing});
      this.navigationService.navigate('compare');
    }
  }

  private getSourcing() {
    const sourcing: IAvailabilitySource[] = [];

    for (const group of this.groups()) {
      if (!group.checked || group.selected_quantity === 0) continue;
      let remaining = group.selected_quantity;
      for (const row of group.sources) {
        const qty = Math.min(remaining, getAvailable(row));
        if (qty > 0) {
          const {checked, ...source} = row;
          sourcing.push({...source, selected_quantity: qty, price: this.priceFor(source.cost)} as IAvailabilitySource);
          remaining -= qty;
        }
        if (remaining === 0) break;
      }
    }

    if (sourcing.length === 0) {
      this.dialogService.alert('Please select a quantity');
      return;
    }

    return sourcing;
  }
}
